import { useRef, useCallback } from 'react';
import classnames from 'classnames';
import type { ProjectGroupRelease } from '../../../../types';
import { RouterLink } from '../../../generic/Link';
import Flex from '../../../generic/Flex';
import Icon, { icons } from '../../../generic/Icon';
import { useOnClickOutside, useInputValue, useOnEscPress } from '../../../../hooks';
import { TextInput } from '../../../generic';

type ProjectGroupNavReleaseSwitcherProps = {
  projectGroupId: number;
  releases: ProjectGroupRelease[];
  currentRelease: ProjectGroupRelease;
  onEscPress: () => void;
  onReleaseSelect: (releaseId: number) => void;
};

/**
 * Displays a dropdown, letting the user select which release group release they want to view.
 *
 * @param {number} projectGroupId - The project group ID.
 * @param {ProjectGroupRelease[]} releases - List of releases for this release group.
 * @param {ProjectGroupRelease} currentRelease - The currently selected release.
 * @param {(releaseId: number) => void} onReleaseSelect - What happens when a release is selected.
 */
const ProjectGroupNavReleaseSwitcher = ({
  projectGroupId,
  releases,
  currentRelease,
  onReleaseSelect,
  onEscPress,
}: ProjectGroupNavReleaseSwitcherProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [searchQuery, onSearchQueryChange] = useInputValue();

  //  Use callback tells React "the reference to this fn may change across renders, but don't re-run the hook".
  const onClose = useCallback(onEscPress, []);

  //  Close the dropdown when we click outside of it.
  useOnClickOutside({ ref, handler: onClose });
  //  Close the dropdown when the user presses the escape key.
  useOnEscPress(onClose);

  //  Handle search filtering
  const filterBySearch = (release: ProjectGroupRelease) =>
    release?.title?.toLowerCase().includes(searchQuery.toLowerCase());
  const filteredReleases = releases.filter(filterBySearch);
  //  "Inject" a release and returns a click handler for that release.
  const onReleaseSelectCurried = (releaseId: number) => () => onReleaseSelect(releaseId);
  return (
    <div ref={ref} className="project-group-nav__switcher">
      <div className="project-group-nav__switcher__row project-group-nav__switcher__heading">
        Switch Release
      </div>
      <div className="project-group-nav__switcher__row">
        <TextInput
          label="Search releases"
          placeholder="Search releases..."
          onChange={onSearchQueryChange}
          value={searchQuery}
          size="small"
        />
      </div>
      <ul className="project-group-nav__switcher__releases">
        {filteredReleases.length === 0
          ? (
            <li className="project-group-nav__switcher__releases__item">
              <div className="project-group-nav__switcher__releases__item__inner">
                No releases found
              </div>
            </li>
          )
          : (
            filteredReleases.map(release => (
              <li
                key={release.id}
                className={classnames('project-group-nav__switcher__releases__item', {
                  'project-group-nav__switcher__releases__item--active': currentRelease && (release.id === currentRelease.id)
                })}
              >
                <div
                  className="project-group-nav__switcher__releases__item__inner"
                  role="button"
                  onClick={onReleaseSelectCurried(release.id)}
                  tabIndex={0}
                >
                  {release.title}
                </div>
              </li>
            ))
          )}
      </ul>
      <div className="project-group-nav__switcher__row pad-top-sm pad-bottom-sm">
        <RouterLink to={`/projects/group/${projectGroupId}/releases`}>
          <Flex>
            <Icon icon={icons.cog} />
            <span className="margin-left-xs">
              Manage Releases
            </span>
          </Flex>
        </RouterLink>
      </div>
    </div>
  );
};

export default ProjectGroupNavReleaseSwitcher;
